<template>
  <div style="margin: 0 auto 5rem; max-width: 960px">
    <HeaderBar
      view="save"
      :showBar="showHeaderBar"
      @discard="discardModalOpen = true"
      @save="saveChanges($route.params.id)"
    />
    <BackButton link="/partners" text="Back to Service partner profiles" />
    <ProfileHeading
      :heading="partnerName"
      :featured-badge="partner.featured"
      :main-badge="{
        text: badgeStatusFilter('status'),
        class: `${badgeStatusFilter('statusClass')}`,
        colors: {
          hiddenStatus: '#fff',
          searchable: '#a4e8f2',
          public: '#aee9d1',
        },
      }"
      :profile-link="{
        href: profileUrl,
        text: isProfilePublic ? 'View profile' : 'Preview profile',
        showPreviewIcon: !isProfilePublic,
      }"
    >
      <div class="flex">
        <div
          v-if="leadFlowEnabled"
          class="flex"
          style="position: absolute; bottom: 0; right: 0.5rem"
        >
          <div
            class="relative inline-block text-left mr-5"
            @mouseover="tooltipTotalLeadsOpen = true"
            @mouseleave="tooltipTotalLeadsOpen = false"
          >
            <div class="flex text-gray-gray hover:text-gray-dark">
              <p class="mr-2 fs-sm">{{ partner.totalLeads }}</p>

              <span>
                <svg
                  class="h-5 w-5"
                  style="margin: 0px"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"
                  />
                </svg>
              </span>

              <Tooltip
                v-if="tooltipTotalLeadsOpen"
                message="Number of leads you've sent to your partner via contact requests."
                css="width: 190px; z-index: 999999999; margin-left:-75px; margin-top:-100px;"
              />
            </div>
          </div>

          <div
            class="relative inline-block text-left mr-5"
            @mouseover="tooltipAcceptedLeadsOpen = true"
            @mouseleave="tooltipAcceptedLeadsOpen = false"
          >
            <div class="flex text-gray-gray hover:text-gray-dark">
              <p class="mr-2 fs-sm">{{ partner.acceptedLeads }}</p>
              <span>
                <svg
                  class="h-5 w-5"
                  style="margin: 0px"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <Tooltip
                v-if="tooltipAcceptedLeadsOpen"
                message="Number of leads accepted by your partner."
                css="width: 190px; z-index: 999999999; margin-left:-75px; margin-top:-100px;"
              />
            </div>
          </div>

          <div
            class="relative inline-block text-left"
            @mouseover="tooltipSecuredLeadsOpen = true"
            @mouseleave="tooltipSecuredLeadsOpen = false"
          >
            <div class="flex text-gray-gray hover:text-gray-dark">
              <p class="mr-2 fs-sm">{{ partner.securedLeads }}</p>

              <span>
                <svg
                  class="h-5 w-5"
                  style="margin: 0px"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fill-rule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>

              <Tooltip
                v-if="tooltipSecuredLeadsOpen"
                message="Number of projects secured by your partner."
                css="width: 180px; z-index: 999999999; margin-left:-80px; margin-top:-100px;"
              />
            </div>
          </div>
        </div>
      </div>
      <Tooltip
        v-if="tooltipPercentOpen"
        :message="partner.completeness | tooltipPercentText"
        :css="partner.completeness | completenessTooltipCss"
      />
    </ProfileHeading>
    <PartnerDetailToolbar :details="details" />

    <hr style="margin: 20px auto 0" />
    <Card>
      <CardHeading heading="About" :showCapacity="false" :showButton="false" />
      <Input
        id="partnerName"
        :value="partner.renderName"
        @input-change="updateProfile"
        name="renderName"
        label="Partner name"
        placeholder="Your partner's company name"
        :required="true"
        :key="nameKey"
        class="mb-4"
      />
      <Input
        id="partnerWebsite"
        :value="partner.renderWebsite"
        @input-change="updateProfile"
        name="renderWebsite"
        label="Partner website"
        placeholder="https://yourwebsite.com"
        :field-validator="validateWebsite"
        :required="true"
        :key="websiteKey"
        class="mb-4"
      />
      <Input
        id="partnerAbout"
        :value="partner.renderAboutUs"
        :limit="1000"
        @input-change="updateProfile"
        name="renderAboutUs"
        type="texteditor"
        placeholder="Briefly tell us about your partner's company"
        label="Partner company description"
        :required="true"
        :key="descriptionKey"
        class="mb-4"
      />
      <FileUploader
        id="partnerLogoImage"
        :existingImg="partner.renderImg"
        @update-image="updateImage"
        field="renderImg"
        buttonText="Replace image"
        buttonType="ghost"
        :forceUpload="true"
        :required="true"
        label="Partner Logo"
        accept=".png, .jpg, .jpeg, .gif"
        zoneText="Click or drag here to upload an image"
        uploadDescription="<p class='fs-sm' style='color: #727a88;'><span style='font-weight: 600;'>File type:</span> JPG, PNG | <span style='font-weight: 600'>Min-width:</span> 400px | <span style='font-weight: 600'>Background:</span> Transparent</p>"
      />
    </Card>

    <!--
      Locations Section
    -->
    <div>
      <Card>
        <CardHeading
          heading="Primary Location"
          :showCapacity="false"
          :showButton="false"
        />
        <div class="grid">
          <div class="grid__cell col4-of-12">
            <Input
              label="Country"
              :showSearchSelect="true"
              :selected="partner.resellerObj.country"
              placeholder="Select a country"
              id="partnerCountryInput"
              :options="countryOptions"
              type="select"
              :required="true"
              @input-change="updateCountry"
              :key="countryKey"
            />
          </div>
          <div class="grid__cell col4-of-12">
            <Input
              label="State/Province"
              placeholder="Select a state/province"
              id="partnerStateInput"
              :showSearchSelect="true"
              type="select"
              :required="partner.resellerObj.country === 'US'"
              :disabled="partner.resellerObj.country !== 'US'"
              :selected="partner.resellerObj.state"
              :options="stateOptions"
              @input-change="updateState"
              :key="stateKey"
            />
          </div>
          <div class="grid__cell col4-of-12">
            <Input
              :value="partner.resellerObj.city"
              @input-change="updateCity"
              label="City"
              :required="true"
              placeholder="City name"
              id="partnerCityInput"
              :key="cityKey"
            />
          </div>
        </div>
      </Card>

      <Card v-if="partner.locationsEnabled">
        <CardHeading
          heading="Additional Locations"
          :showCapacity="false"
          :showButton="!editingLocations"
          buttonText="Add Location"
          @button-click="
            editingLocations = {
              new: true,
              data: { country: {}, region: {}, city: {} },
            }
          "
        />
        <div v-if="!editingLocations && locations[0]">
          <ProfileContentItem
            v-for="(location, i) in locations"
            @edit-click="
              handleAdditionalLocationEdit({
                new: false,
                data: { ...location, order: i, index: i },
              })
            "
            @delete-click="
              deletePrep({
                item: { ...location, order: i, index: i },
                section: 'locations',
                modalTitle: 'Delete location?',
                modalDescription:
                  'This action cannot be undone, are you sure you want to delete this location?',
              })
            "
            :reorderable="false"
            :title="
              `${
                location.region.key
                  ? location.city.key
                    ? location.city.key + ','
                    : 'All Cities, '
                  : ''
              } ${
                location.region.key
                  ? location.region.key
                  : 'All States/Provinces'
              }, ${location.country.key}`
            "
            :key="i"
          />
        </div>
        <CardText
          v-if="!editingLocations && !locations[0]"
          text="No locations have been added."
        />
        <CardFormWrapper v-if="editingLocations">
          <div class="grid">
            <div class="grid__cell col4-of-12">
              <Input
                label="Country"
                :selected="editingLocations.data.country.value"
                :showSearchSelect="true"
                :options="additionalCountryOptions"
                placeholder="Select a country"
                id="partnerAdditionalCountryInput"
                type="select"
                @input-change="updateAdditionalCountry"
              />
            </div>
            <div class="grid__cell col4-of-12">
              <Input
                label="State/Province"
                :selected="editingLocations.data.region.value"
                :showSearchSelect="true"
                :options="additionalStateOptions"
                placeholder="Select State/Province"
                id="partnerAdditionalStateInput"
                type="select"
                @input-change="updateAdditionalState"
              />
            </div>
            <div class="grid__cell col4-of-12">
              <Input
                label="City"
                :selected="editingLocations.data.city.value"
                :showSearchSelect="true"
                :options="additionalCityOptions"
                placeholder="Select city"
                id="partnerAdditionalCityInput"
                type="select"
                @input-change="updateAdditionalCity"
              />
            </div>
          </div>
          <div class="flex" style="justify-content: flex-end; margin-top: 2rem">
            <Button @button-click="editingLocations = false" bType="secondary">
              Cancel
            </Button>
            <Button
              :bDisabled="!locationsFormComplete"
              @button-click="
                handleDoneClick({
                  item: 'editingLocations',
                  section: 'locations',
                })
              "
            >
              Done
            </Button>
          </div>
        </CardFormWrapper>
      </Card>
    </div>

    <!--
      Testimonials Section
    -->
    <div
      style="position: relative"
      @mouseover="testimonialsTooltipOpen = true"
      @mouseleave="testimonialsTooltipOpen = false"
    >
      <Tooltip
        v-if="
          (partner.reviewsEnabled && showTestimonialsTooltip) ||
            testimonialsTooltipOpen
        "
        css="left: 30%; bottom: -130px; z-index: 999;"
        fontClass="text-sm"
        message="Testimonials have been disabled as Reviews are enabled on this Directory"
      />
    </div>
    <div
      :class="partner.reviewsEnabled ? 'disabled' : ''"
      @mouseover="showTestimonialsTooltip = true"
      @mouseleave="hideTestimonialsTooltip"
    >
      <QuoteContentSection
        :service-partner-profile="true"
        :reviews-enabled="partner.reviewsEnabled"
        heading="Testimonials"
        namespace="agencyProfiles/agencyDetail"
        :storeInfo="{
          get: 'testimonials',
          set: 'setTestimonials',
          add: 'addItem',
          edit: 'editItem',
          delete: 'deleteItem',
          dirty: 'markSectionDirty',
        }"
        @delete-content="deletePrep"
      />
    </div>

    <!--
      Featured Content Section
    -->
    <div v-if="partner.showcaseEnabled">
      <MediaContentSection
        namespace="agencyProfiles/agencyDetail"
        heading="Past Work"
        sectionType="pastWork"
        :storeInfo="{
          get: 'pastWork',
          set: 'updatePastWork',
          add: 'addItem',
          edit: 'editItem',
          delete: 'deleteItem',
          dirty: 'markSectionDirty',
        }"
        @delete-content="deletePrep"
      />
      <VideoContentSection
        namespace="agencyProfiles/agencyDetail"
        :service-partner-profile="true"
        @delete-content="deletePrep"
        :videoLimit="3"
        sectionType="videos"
        heading="Videos"
        :storeInfo="{
          get: 'videos',
          set: 'updateVideos',
          add: 'addItem',
          edit: 'editItem',
          delete: 'deleteItem',
          dirty: 'markSectionDirty',
        }"
      />
    </div>

    <!--
      Clients Section
    -->
    <ClientLogoContentSection
      heading="Client Logos"
      namespace="agencyProfiles/agencyDetail"
      :service-partner-profile="true"
      :storeInfo="{
        get: 'clients',
        set: 'updateClients',
        add: 'addItem',
        edit: 'editItem',
        delete: 'deleteItem',
        dirty: 'markSectionDirty',
      }"
      @delete-content="deletePrep"
    />

    <!--
      Resources Section
    -->

    <ResourcesContentSection
      heading="Resources"
      namespace="agencyProfiles/agencyDetail"
      :service-partner-profile="true"
      :storeInfo="{
        get: 'resources',
        set: 'updateResources',
        add: 'addItem',
        edit: 'editItem',
        delete: 'deleteItem',
        dirty: 'markSectionDirty',
      }"
      @delete-content="deletePrep"
    />
    <!--
      Filters
    -->

    <FiltersContentSection
      namespace="agencyProfiles/agencyDetail"
      :service-partner-profile="true"
      :storeInfo="{
        get: 'partner',
      }"
    />

    <div class="buttonsGrid">
      <div class="flex justify-start mt-8">
        <Button
          bType="error"
          class="fl-left fl-none-sm"
          @button-click="removeProfileModal = true"
        >
          Remove partner
        </Button>
      </div>
      <div class="flex mt-8 saveButton">
        <Button @button-click="saveChanges($route.params.id)"> Save </Button>
      </div>
    </div>

    <Modal
      v-if="modalOpen"
      :old="false"
      :newButtons="true"
      :modalShowing="true"
      :title="modalOpen.title"
      :description="modalOpen.description"
      @close-modal="modalOpen = false"
      @primary-click="confirmedDelete()"
      @secondary-click="modalOpen = false"
    />
    <Modal
      v-if="removeProfileModal"
      :modalShowing="true"
      :old="false"
      :newButtons="true"
      title="Remove partner"
      description="This action cannot be undone, are you sure you want to remove this partner?"
      primaryText="Remove"
      secondaryText="Close"
      @primary-click="removePartnerProfile(partner.id)"
      @secondary-click="removeProfileModal = false"
      @close-modal="removeProfileModal = false"
    />
    <Modal
      v-if="discardModalOpen"
      :old="false"
      :newButtons="true"
      :modalShowing="true"
      title="Discard changes?"
      description="Are you sure you want to discard all the changes?"
      @close-modal="discardModalOpen = false"
      primaryText="Discard changes"
      secondaryText="Cancel"
      @primary-click="discardChanges"
      @secondary-click="discardModalOpen = false"
    />
  </div>
</template>

<script>
import PartnerDetailToolbar from '@/components/Dashboard/PartnerDetailToolbar';
const detailPath = 'agencyProfiles/agencyDetail';
import Tooltip from '@/components/DesignSystem/Tooltip';
import Button from '@/components/DesignSystem/Button';
import Card from '@/components/DesignSystem/Card';
import Input from '@/components/DesignSystem/Input';
import Modal from '@/components/DesignSystem/Modal';
import CardHeading from '@/components/DesignSystem/CardHeading';
import FileUploader from '@/components/DesignSystem/FileUploader';
import HeaderBar from '@/components/DesignSystem/HeaderBar';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { validateWebsite } from '@/components/DesignSystem/validators';
import { handleFileChange } from '@/utils/files';
import Vue from 'vue';
import BackButton from '@/components/BackButton';
import ProfileHeading from '@/components/Shared/ContentSection/ProfileHeading';
import QuoteContentSection from '@/components/Shared/ContentSection/QuoteContentSection';
import ResourcesContentSection from '@/components/Shared/ContentSection/ResourcesContentSection';
import ClientLogoContentSection from '@/components/Shared/ContentSection/ClientLogoContentSection';
import FiltersContentSection from '@/components/Shared/ContentSection/FiltersContentSection';
import VideoContentSection from '@/components/Shared/ContentSection/VideoContentSection';
import MediaContentSection from '@/components/Shared/ContentSection/MediaContentSection';
import CardText from '@/components/DesignSystem/CardText';
import CardFormWrapper from '@/components/DesignSystem/CardFormWrapper';
import ProfileContentItem from '@/components/Shared/ProfileContentItem';

export default {
  components: {
    ProfileContentItem,
    CardFormWrapper,
    CardText,
    MediaContentSection,
    VideoContentSection,
    FiltersContentSection,
    ClientLogoContentSection,
    ResourcesContentSection,
    QuoteContentSection,
    ProfileHeading,
    PartnerDetailToolbar,
    Tooltip,
    Button,
    Card,
    Input,
    Modal,
    CardHeading,
    FileUploader,
    HeaderBar,
    BackButton,
  },
  data() {
    return {
      showTestimonialsTooltip: false,
      testimonialsTooltipOpen: false,
      toDelete: undefined,
      discardModalOpen: false,
      modalOpen: false,
      removeProfileModal: false,
      tooltipOpen: false,
      tooltipPercentOpen: false,
      tooltipTotalLeadsOpen: false,
      tooltipAcceptedLeadsOpen: false,
      tooltipSecuredLeadsOpen: false,
      editingLocations: false,
      editingTestimonials: false,
      editingPastWork: false,
      editingVideos: false,
      editingClients: false,
      editingResources: false,
      resourcesRadio: 'link',
      nameKey: 1,
      descriptionKey: 2,
      websiteKey: 3,
      countryKey: 14,
      stateKey: 5,
      cityKey: 6,
      videoKey: 9,
      profileDeleted: false,
    };
  },
  computed: {
    ...mapGetters({
      partner: `${detailPath}/partner`,
      profileUrl: `${detailPath}/profileUrl`,
      isProfilePublic: `${detailPath}/isProfilePublic`,
      filters: `${detailPath}/agencyDetailProfile/getFilters`,
      details: 'details',
      countries: `${detailPath}/mainCountries`,
      additionalCountries: `${detailPath}/agencyDetailLocations/countries`,
      additionalStates: `${detailPath}/agencyDetailLocations/states`,
      additionalCities: `${detailPath}/agencyDetailLocations/cities`,
      states: `${detailPath}/mainStates`,
      testimonials: `${detailPath}/testimonials`,
      showHeaderBar: `${detailPath}/showHeaderBar`,
    }),
    ...mapState(`${detailPath}`, ['errors', 'resourceLinkError']),

    locationsFormComplete() {
      if (this.editingLocations && this.editingLocations.data) {
        if (
          Object.keys(this.editingLocations.data.region).length &&
          Object.keys(this.editingLocations.data.country).length
        ) {
          return true;
        }
      }
      return false;
    },
    displayTooltip() {
      return !(this.partner.status === 'P' || this.partner.status === 'S');
    },
    leadFlowEnabled() {
      return this.$store.getters['details'].leadFlowEnabled;
    },
    partnerName() {
      return this.partner.name ? this.partner.name : '';
    },

    additionalCountryOptions() {
      return this.additionalCountries.map(country => {
        return {
          name: country.name,
          value: country.id,
        };
      });
    },

    additionalStateOptions() {
      var options = [
        {
          name: 'All States/Provinces',
          value: null,
        },
      ];
      this.additionalStates.map(state => {
        return options.push({
          name: state.name,
          value: state.id,
        });
      });
      return options;
    },

    additionalCityOptions() {
      var options = [
        {
          name: 'All Cities',
          value: null,
        },
      ];
      if (this.additionalCities.length) {
        this.additionalCities.map(city => {
          return options.push({
            name: city.name,
            value: city.id,
          });
        });
      }
      return options;
    },

    countryOptions() {
      return this.countries.map(choice => {
        return {
          name: choice[1],
          value: choice[0],
        };
      });
    },

    stateOptions() {
      return this.states.map(choice => {
        return {
          name: choice[1],
          value: choice[0],
        };
      });
    },

    locations: {
      get() {
        return this.$store.getters[
          `${detailPath}/agencyDetailLocations/locations`
        ].filter(i => !i.delete);
      },
      set(value) {
        this.$store.commit(
          `${detailPath}/agencyDetailLocations/setLocations`,
          value
        );
      },
    },
    exisitingLocation() {
      const { countryName, state, city } = this.$store.getters['agencyProfile'];
      return { countryName, state, city };
    },

    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
      };
    },
  },
  filters: {
    tooltipPercentText(value) {
      switch (value) {
        case 0:
          return 'Your invite is still pending';
        case 20:
          return 'Your partner has accepted your invite and started completing their profile. The profile contains partner name, contact name and email address.';
        case 40:
          return 'The profile now also contains their website URL and a logo, a country and state or city.';
        case 60:
          return 'The profile has a summary added too.';
        case 80:
          return 'The profile has at least one filter option selected.';
        case 100:
          return 'All fields have the minimum completed. Yay!';
        default:
          return 'Your invite is still pending';
      }
    },
    completenessTooltipCss(value) {
      switch (value) {
        case 0:
          return 'width: 220px; left: 70px; top: -20px; z-index: 999999999;';
        case 20:
          return 'width: 220px; left: 70px; top: -65px; z-index: 999999999;';
        case 40:
          return 'width: 220px; left: 70px; top: -35px; z-index: 999999999;';
        case 60:
          return 'width: 220px; left: 70px; top: -25px; z-index: 999999999;';
        case 80:
          return 'width: 220px; left: 70px; top: -25px; z-index: 999999999;';
        case 100:
          return 'width: 190px; left: 90px; top: -25px; z-index: 999999999;';
        default:
          return 'width: 220px; left: 70px; top: -25px; z-index: 999999999;';
      }
    },
    status(value) {
      switch (value) {
        case 'I':
          return 'Invited';
        case 'P':
          return 'Public';
        case 'H':
          return 'Hidden';
        case 'S':
          return 'Searchable';
        case 'N':
          return 'Hidden';
        default:
          return 'Invited';
      }
    },
    statusClass(value) {
      switch (value) {
        case 'I':
          return 'invited';
        case 'P':
          return 'public';
        case 'H':
          return 'hiddenStatus';
        case 'S':
          return 'searchable';
        case 'N':
          return 'hiddenStatus';
        default:
          return 'invited';
      }
    },
    filterPdf(value) {
      if (value) {
        if (
          value.slice(-4) === '.pdf' ||
          value.slice(0, 20) === 'data:application/pdf'
        ) {
          return '';
        }
      }
      return value;
    },
  },

  created() {
    this.fetchData();
  },
  beforeRouteLeave(to, from, next) {
    // TODO: ask user if they want to leave with unsaved changes (if unsaved changes)?
    if (!this.profileDeleted) {
      this.discardChanges(false);
    }
    next();
  },

  methods: {
    ...mapActions({
      fetchPartnerDetail: `${detailPath}/agencyDetailProfile/fetchPartnerDetail`,
      updateProfile: `${detailPath}/updateProfile`,
      updateError: `${detailPath}/updateError`,

      fetchMainLocation: `${detailPath}/fetchMainLocation`,
      updateMainLocation: `${detailPath}/updateMainLocation`,
      fetchLocations: `${detailPath}/agencyDetailLocations/fetchLocations`,
      fetchCountries: `${detailPath}/agencyDetailLocations/fetchCountries`,
      fetchStates: `${detailPath}/agencyDetailLocations/fetchStates`,
      fetchCities: `${detailPath}/agencyDetailLocations/fetchCities`,
      clearCities: `${detailPath}/agencyDetailLocations/clearCities`,

      fetchTestimonials: `${detailPath}/fetchTestimonials`,
      fetchFeaturedContent: `${detailPath}/fetchFeaturedContent`,
      fetchClients: `${detailPath}/fetchClients`,
      fetchResources: `${detailPath}/fetchResources`,

      saveChanges: `${detailPath}/saveChanges`,
      updateImage: `${detailPath}/updateImage`,
      deleteItem: `${detailPath}/deleteItem`,
      addItem: `${detailPath}/addItem`,
      editItem: `${detailPath}/editItem`,
      handleCheckboxChange: `${detailPath}/agencyDetailProfile/handleCheckboxChange`,
      handleDropdownChange: `${detailPath}/agencyDetailProfile/handleDropdownChange`,
      removeProfile: `${detailPath}/agencyDetailProfile/removeProfile`,
    }),

    validateWebsite,

    badgeStatusFilter(filter) {
      return this.$options.filters[filter](this.partner.status);
    },

    removePartnerProfile() {
      this.profileDeleted = true;
      this.removeProfile(this.partner.id);
    },

    fetchData() {
      this.fetchPartnerDetail(this.$route.params.id);
      this.fetchMainLocation(this.$route.params.id);
      this.fetchLocations(this.$route.params.id);
      this.fetchTestimonials(this.$route.params.id);
      this.fetchFeaturedContent(this.$route.params.id);
      this.fetchClients(this.$route.params.id);
      this.fetchResources(this.$route.params.id);
      this.fetchCountries();
    },

    async discardChanges(dataFetch = true) {
      if (dataFetch) {
        this.fetchData();
        this.$store.dispatch(`${detailPath}/clearToDelete`);
      } else {
        this.$store.dispatch(`${detailPath}/clearAllData`);
      }

      this.$store.commit(`${detailPath}/clearDirty`);

      this.nameKey += 1;
      this.descriptionKey += 1;
      this.websiteKey += 1;
      this.countryKey += 1;
      this.stateKey += 1;
      this.cityKey += 1;

      this.discardModalOpen = false;
    },
    copyToClipboard(url) {
      navigator.clipboard.writeText(url).then(() => {
        this.$store
          .dispatch('showUserMessage', {
            success: true,
            title: 'Link copied!',
          })
          .catch(() => {
            this.$store.dispatch('showUserMessage', {
              success: false,
              title: 'Sorry!',
              message: 'Something went wrong copying your Get Listed link.',
            });
          });
      });
    },

    ...mapMutations({
      markSectionDirty: `${detailPath}/markSectionDirty`,
    }),

    openPreviewModal() {
      this.modalOpen = true;
    },

    handleFileChange,

    handleEdit(payload) {
      Vue.set(
        this[payload.section]['data'],
        payload.data.key,
        payload.data.value
      );
      if (payload.uri) {
        Vue.set(this[payload.section]['data'], 'uri', payload.uri);
      }
      this.$forceUpdate();
    },

    async handleAdditionalLocationEdit(payload) {
      await this.fetchStates(payload.data.country.value);
      await this.fetchCities({
        stateId: payload.data.region.value,
        countryId: payload.data.country.value,
      });
      this.editingLocations = payload;
    },

    handleDoneClick({ item, section }) {
      if (this[item]['new']) {
        this.addItem({ section, data: this[item]['data'] });
      } else {
        this.editItem({ section, data: this[item]['data'] });
      }
      return (this[item] = false);
    },

    updateCountry(payload) {
      this.updateMainLocation({
        value: payload.value,
        key: 'country',
      });

      if (payload.value !== 'US') {
        this.updateMainLocation({
          value: '',
          key: 'state',
        });
      }
    },

    updateState(payload) {
      this.updateMainLocation({
        value: payload.value,
        key: 'state',
      });
    },

    updateCity(payload) {
      this.updateMainLocation({
        value: payload.value,
        key: 'city',
      });
    },

    updateAdditionalCountry(payload) {
      this.editingLocations.data = {
        ...this.editingLocations.data,
        country: payload,
        region: '',
        city: '',
      };

      if (!this.editingLocations.new) {
        this.editingLocations.data = {
          ...this.editingLocations.data,
          update: true,
        };
      }
      this.fetchStates(payload.value);
    },

    updateAdditionalState(payload) {
      this.editingLocations.data = {
        ...this.editingLocations.data,
        region: payload,
        city: '',
      };
      if (!this.editingLocations.new) {
        this.editingLocations.data = {
          ...this.editingLocations.data,
          update: true,
        };
      }

      if (payload.value != null) {
        this.fetchCities({
          stateId: payload.value,
          countryId: this.editingLocations.data.country.value,
        });
      } else {
        this.clearCities();
      }
    },

    updateAdditionalCity(payload) {
      this.editingLocations.data = {
        ...this.editingLocations.data,
        city: payload,
      };
      if (!this.editingLocations.new) {
        this.editingLocations.data = {
          ...this.editingLocations.data,
          update: true,
        };
      }
    },
    deletePrep(payload) {
      this.toDelete = payload;
      this.modalOpen = {
        title: payload.modalTitle,
        description: payload.modalDescription,
      };
    },

    confirmedDelete() {
      this.$store.dispatch(`${detailPath}/deleteItem`, this.toDelete);
      this.modalOpen = false;
      this.toDelete = false;
    },

    hideTestimonialsTooltip() {
      setTimeout(() => {
        this.showTestimonialsTooltip = false;
      }, 500);
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  margin-top: 20px;
}
.pp-card .wrapper {
  margin-top: 0;
}

.button-type-primary {
  margin-left: 8px;
}

.buttonsGrid {
  display: grid;
  grid-template-columns: 3fr 1fr;
}
.saveButton {
  justify-content: flex-end;
}
@media (max-width: 600px) {
  .buttonsGrid {
    grid-template-columns: 1fr;
  }
  .saveButton {
    justify-content: flex-start;
  }
}
.disabled {
  cursor: not-allowed !important;
}

.route-enter-active,
.route-leave-active {
  transition-duration: 0.1s;
  transition-property: all;
  transition-timing-function: ease;
}

.route-enter-active {
  transition-delay: 0.1s;
}

.route-enter,
.route-leave-active {
  opacity: 0;
}

.fa-angle-left {
  margin-right: 5px;
}

.ghost {
  opacity: 0;
}
</style>
